import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { PotreeCesiumViewerService } from 'src/app/components/potree-cesium-viewer/potree-cesium-viewer.service';
import { AnnotationStoreService } from 'src/app/stores/annotation-store/annotation-store.service';
import { GalleryImagesStoreService } from 'src/app/stores/gallery-images-store/gallery-images-store.service';
import { LayerStoreService } from 'src/app/stores/layer-store/layer-store.service';
import { LocationStoreService } from 'src/app/stores/location-store/location-store.service';
import { AnnotationService } from '../annotation/annotation.service';
import { DataService } from '../data/data.service';
import { FileService } from '../file/file.service';
import { LocationService } from '../location/location.service';
import { MapService } from '../map/map.service';
import { SplitViewService } from '../split-view/split-view.service';

@Injectable({
  providedIn: 'root',
})
export class LocationUIService {
  constructor(
    private locService: LocationService,
    private dataService: DataService,
    private mapService: MapService,
    private annotService: AnnotationService,
    private fileService: FileService,
    private titleService: Title,
    private layerStore: LayerStoreService,
    private potreeCesiumViewerService: PotreeCesiumViewerService,
    private splitViewService: SplitViewService,
    private locationStore: LocationStoreService,
    private annotationStore: AnnotationStoreService,
    private galleryImagesStore: GalleryImagesStoreService
  ) {}

  /**
   * Refreshes the current location with latest info
   * (after update of any of its subproperties)
   *
   * @param id Optional for initialization
   */
  async refreshCurrLocation(id?: string): Promise<void> {
    const unsub$ = new Subject();
    this.mapService.isMapLoaded$
      .pipe(takeUntil(unsub$.asObservable()))
      .subscribe(async (isLoaded) => {
        if (!isLoaded) return;

        unsub$.next(true);
        unsub$.unsubscribe();

        const prevLoc = this.locationStore.currentLocation;
        let locId: string = id || prevLoc.id;

        let allLocations = this.locationStore.allLocations;
        if (id) {
          let location = allLocations[id];
          if (location) {
            let locations = [location.id];
            if (location.children?.length > 0) locations = location.children;
            this.locationStore.setLocations = locations;
            this.dataService.allDefectCounts$.next(location.defectSeverities);
          } else {
            await this.refreshDefectCounts(id);
            allLocations = this.locationStore.allLocations;
          }
        }

        // if (prevLoc.id) {
        //   this.mapService.removeOrtho(prevLoc.id);
        //   this.mapService.removeDSM(prevLoc.id);
        //   this.mapService.removeThermal(prevLoc.id);
        //   this.mapService.removeThreeDMesh(prevLoc.id);
        //   this.potreeCesiumViewerService.unloadAllPointClouds()
        // }
        // if (prevLoc.id && prevLoc.map_layer.find((i) => i.layer_type === 'ortho'))
        //   this.mapService.removeOrtho(prevLoc.id);
        // if (prevLoc.id && prevLoc.map_layer.find((i) => i.layer_type === 'dsm'))
        //   this.mapService.removeDSM(prevLoc.id);
        // if (prevLoc.id && prevLoc.map_layer.find((i) => i.layer_type === 'thermal'))
        //   this.mapService.removeThermal(prevLoc.id);
        // if (prevLoc.id && prevLoc.map_layer.find((i) => i.layer_type === '3d-mesh'))
        //   this.mapService.removeThreeDMesh(prevLoc.id);
        // if (prevLoc.id && prevLoc.map_layer.find((i) => i.layer_type === '3d'))
        //   this.potreeCesiumViewerService.unloadAllPointClouds()

        let currLoc = allLocations[locId];

        //populate marker
        let locations = [];
        if (currLoc?.children && currLoc.children.length > 0) {
          for (const childId of currLoc.children) {
            if (allLocations[childId]) locations.push(allLocations[childId]);
          }
        }

        this.mapService
          .getLocation(locations)
          .pipe(take(1))
          .subscribe((location) => {
            this.dataService.allVisibleMarkers$.next(location);
            this.mapService.makeMarkers(
              location,
              undefined,
              currLoc.default_zoom_level
            );
          });
        this.mapService.removeSpotlight3D();

        if (!locId)
          throw new Error('ID is missing. Unable to refresh current location');

        this.locService
          .findOne(locId)
          .pipe(take(1))
          .subscribe((res) => {
            currLoc = res;
            this.titleService.setTitle(`${currLoc.name} | Vertikaliti`);
            if (!currLoc.show_annotation_marker) {
              if (this.mapService.map.getLayer('annotation-markers-layer'))
                this.mapService.map.removeLayer('annotation-markers-layer');
              if (this.mapService.map.getSource('annotation-markers-source'))
                this.mapService.map.removeSource('annotation-markers-source');
            }
            // if (prevLoc.id != locId)
            this.locationStore.setCurrentLocation = currLoc;

            /**
             * load map_layer_ortho if available
             */
            if (currLoc?.map_layer_ortho) {
              if (this.splitViewService.isSplitscreenOn) {
                for (
                  let i = 0;
                  i < this.splitViewService.mapViews.length;
                  i++
                ) {
                  if (i === 0 && !this.splitViewService.mapViews[0].ortho) {
                    this.splitViewService.mapViews[0].ortho =
                      currLoc.map_layer_ortho;
                  }
                  const ortho = this.splitViewService.mapViews[i].ortho;
                  if (ortho) {
                    console.log('addOrtho1');
                    this.mapService.addOrtho(
                      ortho,
                      ortho.id,
                      this.splitViewService.mapViews[i].map
                    );
                  }
                }
              } else {
                // get pinned ortho
                // if pinned ortho null, show first ortho
                const getPinnedOrtho = currLoc.pinned_map_layer_type?.filter(
                  (i) => i.includes('ortho')
                );
                if (getPinnedOrtho && getPinnedOrtho.length > 0) {
                } else {
                  this.splitViewService.mapViews[0].ortho =
                    currLoc.map_layer_ortho;
                  this.layerStore.orthoManyLayerFilter$.next([
                    currLoc.map_layer_ortho.id,
                  ]);
                  this.mapService.addOrtho(
                    currLoc.map_layer_ortho,
                    currLoc.map_layer_ortho.id
                  );
                }
                this.layerStore.isOrtho$.next(true);
              }
              this.splitViewService.onEvent$.next({
                refreshAnchorButton: true,
              });
            }

            /**
             * load pinned map_layer on init and if splitscreen off
             */
            // if (
            //   !this.splitViewService.isSplitscreenOn &&
            //   currLoc?.pinned_map_layer_type?.length > 0
            // ) {
            //   const type = currLoc.pinned_map_layer_type[0];
            //   const map_layer = `map_layer_${type
            //     .replace('-', '_')
            //     .toLowerCase()}`;
            //   if (currLoc[map_layer]?.external_url) {
            //     this.layerStore.toggleMapLayer(
            //       type,
            //       currLoc.id,
            //       currLoc[map_layer],
            //       true
            //     );
            //   }
            // }

            /** Get annotation count */
            this.annotService
              .findAll([
                { key: 'filtered[0][key]', val: 'location' },
                { key: 'filtered[0][val]', val: currLoc.id },
                { key: 'sorted[0][key]', val: 'created_at' },
                { key: 'sorted[0][val]', val: '-1' },
              ])
              .pipe(take(1))
              .subscribe((annot) => {
                this.annotationStore.setAnnotations = annot.data;
                // if (annot.data.length === 0) this.mapService.centerThenZoom(currLoc.coordinate, 5)
                this.layerStore.toggleAllAnnotationDataLayer(
                  this.layerStore.isAnnotation$.getValue()
                );
              });

            this.fileService
              .findAll([
                { key: 'filtered[0][key]', val: 'location' },
                { key: 'filtered[0][val]', val: currLoc.id },
                { key: 'filtered[1][key]', val: 'tag' },
                { key: 'filtered[1][val][0]', val: 'images' },
                { key: 'filtered[1][val][1]', val: 'videos' },
              ])
              .subscribe((res) => {
                this.galleryImagesStore.setGalleryImages = res.data;
              });
            // }
          });
      });
  }

  async refreshDefectCounts(locationId?: string) {
    const usersLocations = await this.locService.getSummaries().toPromise();
    const allLocations = usersLocations.allLocations;
    this.locationStore.allLocations = allLocations;
    this.dataService.allDefectCounts = usersLocations.defectSeverities;
    this.dataService.allDefectCounts$.next(usersLocations.defectSeverities);

    if (locationId) {
      let location = allLocations[locationId];
      let locations = [location.id];
      if (location.children?.length > 0) locations = location.children;
      this.locationStore.setLocations = locations;
      this.dataService.allDefectCounts$.next(location.defectSeverities);
    } else {
      this.locationStore.setLocations = allLocations['parents'];
    }
  }
}
